import { useEffect, useState } from "react";
import { Clock, Gift } from "lucide-react";
import { LimitedTag } from "@/components/LimitedTag";
import { api, fmtKST, money, won, previewUrl } from "@/lib/api";
import { EmptyState } from "@/components/Bits";

const countdown = (ms) => {
  if (ms <= 0) return "Opening now";
  const m = Math.ceil(ms / 60000);
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), mm = m % 60;
  return `Opens in ${d ? `${d}d ` : ""}${d || h ? `${h}h ` : ""}${mm}m`;
};

const TeaserCard = ({ d, now, i }) => {
  const img = previewUrl(d);
  return (
  <div data-testid={`upcoming-drop-${d.id}`} style={{ animationDelay: `${Math.min(i, 9) * 50}ms` }}
    className="rise relative overflow-hidden rounded-2xl border border-dashed border-sky-400/30 bg-sky-500/[0.04]">
    {img && <img data-testid={`upcoming-preview-${d.id}`} src={img} alt="" className="h-40 w-full object-cover border-b border-sky-400/20" />}
    <div className="p-6">
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-500/15 px-2.5 py-0.5 text-xs font-semibold text-sky-200"><Clock className="h-3.5 w-3.5" /> Coming soon</span>
      {d.round_type && <span className="text-xs rounded-full bg-pink-500/15 text-pink-200 px-2 py-0.5 font-semibold">{d.round_type}</span>}
      {d.album_type && <span className="text-xs rounded-full bg-violet-500/15 text-violet-200 px-2 py-0.5 font-semibold">{d.album_type}</span>}
      {d.bonus_items && <span data-testid={`upcoming-bonus-tag-${d.id}`} className="inline-flex items-center gap-1 text-xs rounded-full bg-amber-400/15 text-amber-200 px-2 py-0.5 font-semibold"><Gift className="h-3 w-3" />Bonus</span>}
      {d.limited && <LimitedTag testId={`upcoming-limited-tag-${d.id}`} />}
    </div>
    <h3 className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-4">{d.store || d.title}</h3>
    {d.ver_name && <div className="text-sm text-zinc-400">{d.ver_name}</div>}
    <div data-testid={`upcoming-price-${d.id}`} className="mt-3 text-sm">
      <span className="font-mono font-semibold">{money(d.price_per_member)}</span>
      {d.price_won != null && <span className="font-mono text-zinc-400"> · {won(d.price_won)}</span>}
      <span className="text-zinc-500 text-xs"> /{d.album_type === "Unit Digi" ? "unit" : "member"}</span>
    </div>
    <div data-testid={`upcoming-countdown-${d.id}`} className="mt-5 font-mono text-2xl font-bold text-sky-300">{countdown(new Date(d.opens_at) - now)}</div>
    <div className="text-xs text-zinc-500 mt-1">{fmtKST(d.opens_at)}</div>
    </div>
  </div>
  );
};

export const UpcomingDrops = ({ onOpened, emptyHint = false }) => {
  const [list, setList] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    api.get("/drops/upcoming").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (list?.some((d) => new Date(d.opens_at) <= now)) {
      setList((l) => l.filter((d) => new Date(d.opens_at) > now));
      onOpened();
    }
  }, [now, list, onOpened]);
  if (list === null) return emptyHint ? <div className="mt-8 text-zinc-500 text-sm">Loading…</div> : null;
  if (!list.length) return emptyHint ? <div className="mt-8"><EmptyState testId="upcoming-empty" title="Nothing coming soon yet" hint="Scheduled drops will show here with a countdown." /></div> : null;
  return (
    <section className="mt-8" data-testid="upcoming-drops-section">
      <h2 className="font-display text-base md:text-lg font-bold">Coming Soon</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-3">{list.map((d, i) => <TeaserCard key={d.id} d={d} now={now} i={i} />)}</div>
    </section>
  );
};
